import axios, { AxiosRequestConfig } from "axios";
import { getAuthTokenForRequest } from "@/lib/authTokenForApi";
import {
  LEGAL_CONTENT_ALIASES_BY_PRIORITY,
  selectCanonicalLegalContent,
} from "@/lib/legalContent";

export type GlobalLegalSettingKey =
  | "privacy_policy"
  | "terms_and_conditions"
  | "cookie_policy";

export interface GlobalLegalSettingContent {
  content: string;
  resolvedKey: string | null;
  modifiedDatetime: string | null;
}

const GLOBAL_LEGAL_BY_KEY_ENDPOINTS = [
  "/api/v1/global_legal_policy/by_key",
] as const;

const KEY_CANDIDATES: Record<GlobalLegalSettingKey, string[]> = {
  privacy_policy: ["privacy"],
  terms_and_conditions: ["terms"],
  cookie_policy: ["cookie"],
};

const KEY_ALIASES = ["key", "policy_key", "legal_policy_key", "p_key"] as const;
const CONTENT_ALIASES = LEGAL_CONTENT_ALIASES_BY_PRIORITY;
const MODIFIED_DATETIME_ALIASES = [
  "modified_datetime",
  "updated_at",
  "modified_at",
  "created_at",
  "created_datetime",
] as const;

function getBaseUrl() {
  const configured = process.env.NEXT_PUBLIC_API_URL || "http://localhost:3000/api";
  return configured.replace(/\/api\/?$/, "");
}

function getRequestConfig(): AxiosRequestConfig {
  const token = getAuthTokenForRequest();

  return {
    baseURL: getBaseUrl(),
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Token: String(token) } : {}),
    },
  };
}

function getFirstString(
  source: Record<string, unknown>,
  keys: readonly string[],
): string | null {
  for (const key of keys) {
    const value = source[key];

    if (typeof value === "string" && value.trim().length > 0) {
      return value;
    }

    if (typeof value === "number" || typeof value === "boolean") {
      return String(value);
    }
  }

  return null;
}

function toObjectArray(payload: unknown): Array<Record<string, unknown>> {
  if (Array.isArray(payload)) {
    return payload.filter(
      (entry): entry is Record<string, unknown> =>
        !!entry && typeof entry === "object",
    );
  }

  if (!payload || typeof payload !== "object") {
    return [];
  }

  const record = payload as Record<string, unknown>;
  const nestedCandidates = [record.data, record.result, record.record];

  for (const nested of nestedCandidates) {
    const nestedRecords = toObjectArray(nested);
    if (nestedRecords.length > 0) {
      return nestedRecords;
    }
  }

  return [record];
}

function parseGlobalLegalSettingPayload(payload: unknown): GlobalLegalSettingContent {
  const records = toObjectArray(payload);

  for (const record of records) {
    const content = selectCanonicalLegalContent(record, CONTENT_ALIASES);

    if (content && content.trim().length > 0) {
      return {
        content,
        resolvedKey: getFirstString(record, KEY_ALIASES),
        modifiedDatetime: getFirstString(record, MODIFIED_DATETIME_ALIASES),
      };
    }
  }

  return { content: "", resolvedKey: null, modifiedDatetime: null };
}

export async function fetchGlobalLegalSettingByKey(
  key: GlobalLegalSettingKey,
): Promise<GlobalLegalSettingContent> {
  const keyCandidates = KEY_CANDIDATES[key];
  let firstSuccessfulEmpty: GlobalLegalSettingContent | null = null;
  let lastError: unknown = null;

  for (const candidate of keyCandidates) {
    for (const endpoint of GLOBAL_LEGAL_BY_KEY_ENDPOINTS) {
      try {
        const response = await axios.get(endpoint, {
          ...getRequestConfig(),
          params: { p_key: candidate },
        });

        const parsed = parseGlobalLegalSettingPayload(response.data);
        const normalized = {
          content: parsed.content,
          resolvedKey: parsed.resolvedKey ?? candidate,
          modifiedDatetime: parsed.modifiedDatetime,
        };

        if (normalized.content.trim().length > 0) {
          return normalized;
        }

        if (!firstSuccessfulEmpty) {
          firstSuccessfulEmpty = normalized;
        }
      } catch (error) {
        lastError = error;
      }
    }
  }

  if (firstSuccessfulEmpty) {
    return firstSuccessfulEmpty;
  }

  if (lastError) {
    throw lastError;
  }

  return { content: "", resolvedKey: null, modifiedDatetime: null };
}
